import React, { useEffect, useState } from 'react';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';
import '../../styles/realsignal-theme.css';

interface BillingSuccessProps {
    sessionId: string;
    onVerified?: (tokens: { accessToken: string; refreshToken: string }) => void;
    onError?: (error: string) => void;
}

const colors = {
    background: '#0F1A24',
    backgroundLight: '#162434',
    backgroundCard: '#1C2E42',
    textPrimary: '#E6EBF0',
    textSecondary: '#A9B4C2',
    textMuted: '#7F8C9C',
    accent: '#3A6A7A',
    accentLight: '#4A8A9A',
    border: '#1C2E42',
    success: '#10B981',
    error: '#EF4444',
};

type VerificationStatus = 'verifying' | 'success' | 'error';

export const BillingSuccess: React.FC<BillingSuccessProps> = ({
    sessionId,
    onVerified,
    onError,
}) => {
    const [status, setStatus] = useState<VerificationStatus>('verifying');
    const [errorMessage, setErrorMessage] = useState<string>('');

    useEffect(() => {
        const verifyPayment = async () => {
            try {
                const response = await fetch('/api/realsignal/billing/verify', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ sessionId }),
                });

                const data = await response.json();

                if (data.success) {
                    setStatus('success');
                    if (onVerified && data.data?.tokens) {
                        // Store tokens
                        localStorage.setItem(
                            'realsignal_access_token',
                            data.data.tokens.accessToken
                        );
                        localStorage.setItem(
                            'realsignal_refresh_token',
                            data.data.tokens.refreshToken
                        );
                        onVerified(data.data.tokens);
                    }
                } else {
                    setStatus('error');
                    setErrorMessage(data.error || 'Payment verification failed');
                    onError?.(data.error || 'Payment verification failed');
                }
            } catch (error) {
                setStatus('error');
                const message = error instanceof Error ? error.message : 'Network error';
                setErrorMessage(message);
                onError?.(message);
            }
        };

        if (sessionId) {
            verifyPayment();
        } else {
            setStatus('error');
            setErrorMessage('No session ID provided');
        }
    }, [sessionId, onVerified, onError]);

    return (
        <div
            className="min-h-screen flex items-center justify-center"
            style={{
                background: colors.background,
                fontFamily:
                    "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            }}
        >
            <div className="w-full max-w-md mx-auto px-6 py-8">
                {/* Logo */}
                <div className="text-center mb-8">
                    <div className="flex justify-center mb-4">
                        <img
                            src="/assets/realsignal-logo.png"
                            alt="RealSignal"
                            className="h-12 w-auto"
                            style={{ filter: 'brightness(1.15)' }}
                        />
                    </div>
                </div>

                {/* Status Card */}
                <div
                    style={{
                        background: `linear-gradient(145deg, ${colors.backgroundCard} 0%, ${colors.backgroundLight} 100%)`,
                        border: `1px solid ${colors.border}`,
                        borderRadius: '2rem',
                        padding: '2.5rem',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                        textAlign: 'center',
                    }}
                >
                    {status === 'verifying' && (
                        <>
                            <Loader2
                                className="w-16 h-16 mx-auto mb-6 animate-spin"
                                style={{ color: colors.accent }}
                            />
                            <h2
                                style={{
                                    fontSize: '1.5rem',
                                    fontWeight: 700,
                                    color: colors.textPrimary,
                                    marginBottom: '0.5rem',
                                }}
                            >
                                Verifying Payment
                            </h2>
                            <p style={{ color: colors.textSecondary }}>
                                Please wait while we confirm your subscription...
                            </p>
                        </>
                    )}

                    {status === 'success' && (
                        <>
                            <CheckCircle
                                className="w-16 h-16 mx-auto mb-6"
                                style={{ color: colors.success }}
                            />
                            <h2
                                style={{
                                    fontSize: '1.5rem',
                                    fontWeight: 700,
                                    color: colors.textPrimary,
                                    marginBottom: '0.5rem',
                                }}
                            >
                                Payment Successful
                            </h2>
                            <p style={{ color: colors.textSecondary, marginBottom: '1.5rem' }}>
                                Your RealSignal Pro subscription is now active.
                            </p>
                            <p
                                style={{
                                    fontSize: '0.875rem',
                                    color: colors.textMuted,
                                }}
                            >
                                Redirecting to dashboard...
                            </p>
                        </>
                    )}

                    {status === 'error' && (
                        <>
                            <XCircle
                                className="w-16 h-16 mx-auto mb-6"
                                style={{ color: colors.error }}
                            />
                            <h2
                                style={{
                                    fontSize: '1.5rem',
                                    fontWeight: 700,
                                    color: colors.textPrimary,
                                    marginBottom: '0.5rem',
                                }}
                            >
                                Verification Failed
                            </h2>
                            <p style={{ color: colors.textSecondary, marginBottom: '1.5rem' }}>
                                {errorMessage}
                            </p>
                            <button
                                onClick={() => (window.location.href = '/realsignal')}
                                style={{
                                    background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentLight} 100%)`,
                                    color: 'white',
                                    fontWeight: 600,
                                    padding: '0.75rem 1.5rem',
                                    borderRadius: '1rem',
                                    border: 'none',
                                    cursor: 'pointer',
                                }}
                            >
                                Return to Sign In
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BillingSuccess;
